/** @jsx jsx */
import { jsx } from "theme-ui";
import React, { useEffect, useState } from "react";
import {
  bookTicketWrapStyles,
  containerStyles,
  navigationWrap,
  wrapStyles,
  headerTopPaddingStyles,
} from "./styles";
import { Box } from "@shared-components/Box/Box";
import { LinkCollectionLink } from "../../carlsberg-headless/types/LinkCollection";
import { Link } from "@shared-components/Link/Link";
import { useSiteContext } from "../../context/site/useSiteContext";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { OpeningHour } from "../../carlsberg-headless/types/OpeningHour";
import { Nullable } from "../../shared/types/util";
import { ButtonLink } from "@shared-components/Button/Button";
import { TaxonomiesField } from "../../carlsberg-headless/types/fields/Taxonomies";
import { ImageField } from "src/carlsberg-headless/types/fields/Image";
import { Image } from "@shared-components/Image/Image";
import { TAXONOMIES_MAP } from "../../carlsberg-headless/taxonomies-map";
import { Icon } from "@shared-components/Icon/Icon";
import TicketIcon from "@svg-icons/ticket.component.svg";
import { useWindowSize } from "../../shared/hooks/useWindowSize/useWindowSize";
import { buttonTextStyles } from "@shared-components/Button/styles";

export interface HeaderProps {
  navigation?: React.ReactElement;
  bookTicketLink?: LinkCollectionLink;
  cultureOpen?: string;
  openingHours?: Nullable<OpeningHour[]>;
  currentFrontpageUrl?: string;
  taxonomies?: TaxonomiesField;
  companyLogoPrimary?: ImageField;
  hideBookTicket?: boolean;
}

const PureHeader: React.FC<HeaderProps> = ({
  navigation,
  bookTicketLink,
  currentFrontpageUrl,
  companyLogoPrimary,
  hideBookTicket = false,
}) => {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width && windowSize.width < 1040;
  const [hideOnScrollBookTicket, setHideOnScrollBookTicket] = useState(false);

  useEffect(() => {
    const heroContent = document.getElementById("hero-content");

    const handleScroll = () => {
      if (heroContent) {
        const heroRect = heroContent.getBoundingClientRect();
        setHideOnScrollBookTicket(heroRect.bottom <= 70);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box sx={wrapStyles}>
      <Box sx={headerTopPaddingStyles} />
      {!hideBookTicket && bookTicketLink?.link?.url && (
        <Box sx={bookTicketWrapStyles}>
          <ButtonLink
            to={bookTicketLink.link.url}
            {...(bookTicketLink.link.target && {
              target: bookTicketLink.link.target,
            })}
            variant="whiteOutline"
            outline
            sx={{
              ...(isMobile &&
                !hideOnScrollBookTicket && {
                  display: "none",
                }),
              transition:
                "width 200ms linear, background-position 300ms ease-in-out",
              overflow: "hidden",
              width: "100%",
              backgroundImage:
                "linear-gradient(to bottom, transparent 0%, transparent 50%, #F8F8F8 50%, #F8F8F8 100%)",

              p: "0",
              px: ["10px", null],
              boxShadow: "0 0 0 11px rgba(0, 0, 0, 0.07)",
              ml: [null, "11px"],
              "> span": {
                color: "white",
              },

              "> span:first-of-type": {
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "50px",
                height: "50px",
              },
              "svg path.ticket_component_svg__flawer": {
                fill: "white  ",
              },
              "&:hover, &:focus": {
                boxShadow: "0 0 0 11px rgba(0, 0, 0, 0.07)",
                "svg path": {
                  stroke: "black",
                  fill: "none",
                  "&.ticket_component_svg__flawer": {
                    fill: "black",
                  },
                },
              },
              "@media screen and (max-width: 576px)": {
                display: "none",
              },
              "&.active": {
                border: "0",

                width: "50px",
                height: "50px",
                backgroundImage:
                  "linear-gradient(to bottom, #fff 0%, #fff 50%, #284A41 50%, #284A41 100%)", //#F8F8F8

                "svg path": {
                  stroke: "#284A41",
                  fill: "none",
                  "&.ticket_component_svg__flawer": {
                    fill: "#284A41",
                  },
                },
                "&:hover, &:focus": {
                  "svg path": {
                    stroke: "white",
                    fill: "none",
                    "&.ticket_component_svg__flawer": {
                      fill: "white",
                    },
                  },
                },
              },
            }}
            className={hideOnScrollBookTicket ? "active" : ""}
          >
            <Box
              as="span"
              sx={{ display: "block", width: "40px", height: "24px" }}
            >
              <Icon
                sx={{
                  strokeWidth: "1.5px",
                  stroke: "white",
                  fill: "none",
                  width: "24px",
                  height: "24px",
                }}
                svg={TicketIcon}
              />
            </Box>
            <Box
              as="span"
              sx={{
                ...buttonTextStyles,
                transition: "opacity 300ms linear",
                opacity: hideOnScrollBookTicket ? 0 : 1,
                // pl: hideOnScrollBookTicket ? 0 : "16px",
                width: hideOnScrollBookTicket ? 0 : "100%",
                display: "inline-block",
                minWidth: hideOnScrollBookTicket ? 0 : "100px",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {bookTicketLink.link.name ?? bookTicketLink.link.name}
            </Box>
          </ButtonLink>
        </Box>
      )}

      <Box sx={containerStyles}>
        <Link
          to={
            currentFrontpageUrl?.startsWith("/")
              ? currentFrontpageUrl
              : `/${currentFrontpageUrl}`
          }
          sx={{
            ":hover, :focus": {
              boxShadow: "none",
              transition: "opacity 200ms",
              opacity: 0.8,
            },
          }}
        >
          {companyLogoPrimary?.fullUrl && (
            <Image
              src={companyLogoPrimary?.fullUrl}
              alt="logo"
              sx={{ width: ["140px", null, "150px"] }}
            />
          )}
        </Link>
      </Box>
      <Box sx={navigationWrap}>{navigation}</Box>
    </Box>
  );
};

const Header: React.FC<HeaderProps> = (props) => {
  const { get } = useDictionaryContext();
  const {
    currentCulture,
    bookTicketLink,
    currentFrontpageUrl,
    pageTaxonomies,
  } = useSiteContext();

  return (
    <PureHeader
      bookTicketLink={bookTicketLink?.links?.[0]}
      cultureOpen={get(DICTIONARY_MAP.opening_hours.open, currentCulture)}
      currentFrontpageUrl={currentFrontpageUrl}
      taxonomies={pageTaxonomies}
      hideBookTicket={pageTaxonomies.includes(
        TAXONOMIES_MAP.page.page_hide_book_ticket_desktop
      )}
      {...props}
    />
  );
};

export { Header, PureHeader };
